import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Users, TrendingUp, CalendarDays, Clock3 } from "lucide-react";

function monthLabel(month, lang) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(lang === "ms" ? "ms-MY" : "en-MY", {
    month: "long",
    year: "numeric",
  });
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ClassReportPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const myClasses = useMemo(() => {
    if (!user) return [];
    if (user.role === "teacher") return classes.filter((c) => c.teacher_id === user.id);
    return classes;
  }, [classes, user]);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!classId && myClasses.length > 0) setClassId(myClasses[0].id);
  }, [myClasses, classId]);

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    api
      .get("/reports/class-attendance", { params: { class_id: classId, month } })
      .then((r) => setReport(r.data))
      .catch((err) => {
        setReport(null);
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      })
      .finally(() => setLoading(false));
  }, [classId, month]);

  const dayNames = lang === "ms"
    ? ["Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu", "Ahad"]
    : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  return (
    <div className="space-y-6" data-testid="class-report">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("classReport")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("classReportSub")}</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={classId} onValueChange={setClassId}>
          <SelectTrigger className="h-11 w-64 rounded-xl" data-testid="report-class-select">
            <SelectValue placeholder={t("chooseClass")} />
          </SelectTrigger>
          <SelectContent>
            {myClasses.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name} — {c.level}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 rounded-xl border border-border/70 bg-background px-1 py-1">
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="report-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="num min-w-36 text-center text-sm font-semibold">{monthLabel(month, lang)}</span>
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="report-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {myClasses.length === 0 && (
        <Card className="rounded-2xl"><CardContent className="p-6 text-sm text-muted-foreground">
          {lang === "ms" ? "Tiada kelas ditugaskan kepada anda lagi." : "No classes assigned to you yet."}
        </CardContent></Card>
      )}

      {report && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Card className="rounded-2xl"><CardContent className="p-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4 text-primary" /> {t("totalStudents")}</div>
              <div className="num mt-2 text-4xl font-semibold" data-testid="report-total-students">{report.total_students}</div>
            </CardContent></Card>
            <Card className="rounded-2xl"><CardContent className="p-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><TrendingUp className="h-4 w-4 text-primary" /> {t("classAverage")}</div>
              <div className="num mt-2 text-4xl font-semibold text-primary" data-testid="report-class-average">{report.class_average_rate}%</div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${report.class_average_rate}%` }} />
              </div>
            </CardContent></Card>
            <Card className="rounded-2xl col-span-2 lg:col-span-1"><CardContent className="p-5">
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="h-4 w-4 text-primary" /> {report.class?.name}</div>
              <div className="mt-2 text-sm">
                <span className="font-semibold">{dayNames[report.class?.day_of_week ?? 0]}</span>
                <span className="num text-muted-foreground"> · {report.class?.start_time}–{report.class?.end_time}</span>
                {report.class?.room && <span className="text-muted-foreground"> · {report.class.room}</span>}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{report.class?.teacher_name}</div>
            </CardContent></Card>
          </div>

          {/* Per-student table */}
          <Card className="rounded-2xl"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("student")}</TableHead>
                <TableHead className="text-center">{t("present")}</TableHead>
                <TableHead className="text-center">{t("late")}</TableHead>
                <TableHead className="text-center">{t("absent")}</TableHead>
                <TableHead className="text-center">{t("markedDaysCol")}</TableHead>
                <TableHead className="w-48">{t("rate")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {report.students.map((s) => (
                  <TableRow key={s.student_id} data-testid={`report-row-${s.student_id}`}>
                    <TableCell className="font-medium">{s.student_name}</TableCell>
                    <TableCell className="num text-center text-primary font-semibold">{s.present}</TableCell>
                    <TableCell className="num text-center text-amber-500 font-semibold">{s.late}</TableCell>
                    <TableCell className="num text-center text-destructive font-semibold">{s.absent}</TableCell>
                    <TableCell className="num text-center">{s.marked_days}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full rounded-full transition-[width] duration-500 ${s.attendance_rate >= 80 ? "bg-primary" : s.attendance_rate >= 50 ? "bg-amber-400" : "bg-destructive"}`}
                            style={{ width: `${s.attendance_rate}%` }}
                          />
                        </div>
                        <span className="num w-10 text-right text-xs font-semibold">{s.attendance_rate}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {report.students.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent></Card>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock3 className="h-3.5 w-3.5" />
            {lang === "ms"
              ? "Kadar = (hadir + lewat) / hari direkod dalam bulan dipilih."
              : "Rate = (present + late) / days recorded in the selected month."}
          </div>
        </>
      )}

      {loading && !report && <p className="text-sm text-muted-foreground">…</p>}
    </div>
  );
}
